/**
 * cardMailService — the Work card's window onto the Gmail thread it was
 * captured from (slice 4 of the mail arc, 2026-09-05).
 *
 * Two callables, deliberately narrow, both gated server-side by the
 * send-email permission (admins/owners, or an employee with
 * `permissions.sendEmail`) — reading a parent's thread is as sensitive as
 * replying to it. The browser never holds the Gmail credential; these are
 * callables for exactly that reason, and there is no direct Firestore read of
 * mail here because mail is not in Firestore.
 *
 * Nothing here sends. `createCardMailDraft` puts a byte-verified reply in
 * Gmail Drafts on the card's thread and returns the link; sending stays a
 * human act in Gmail.
 */

import { functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';

/** The thread behind a card: { taskId, threadId, thread: { subject, messages[] } | null, mailReply, gmailUrl }. */
export async function getCardMailThread(taskId) {
  const callable = httpsCallable(functions, 'getCardMailThread');
  const res = await callable({ taskId });
  return res.data;
}

/**
 * Draft a reply on the card's thread. `html` is the body; `text` optional.
 * Recipient defaults to the family's last message on the thread; subject to
 * "Re: <thread subject>". Returns mail_create_draft's result (draftId,
 * gmailUrl, verified.checks, …).
 */
export async function createCardMailDraft({ taskId, html, text, to, subject, sendAs }) {
  const callable = httpsCallable(functions, 'createCardMailDraft');
  const res = await callable({ taskId, html, text, to, subject, sendAs });
  return res.data;
}

/**
 * Turn a plain-text reply into the minimal HTML a Gmail reply carries:
 * escaped, paragraphs on blank lines, line breaks within. No shell — a 1:1
 * reply on an existing thread reads as a reply, not a newsletter.
 */
export function plainTextToReplyHtml(text) {
  const esc = (s) =>
    String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  const paras = String(text || '')
    .replace(/\r\n/g, '\n')
    .trim()
    .split(/\n{2,}/)
    .map((p) => `<p style="margin:0 0 1em">${esc(p).replace(/\n/g, '<br>')}</p>`);
  return `<div style="font-family:Arial,Helvetica,sans-serif;font-size:15px;line-height:1.5;color:#1b2a49">${paras.join('')}</div>`;
}
